import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import ko from './locales/ko.json';
import en from './locales/en.json';
import ja from './locales/ja.json';

export type Locale = 'ko' | 'ja' | 'en';
export type Messages = typeof ko;

const resources: Record<Locale, Messages> = {
  ko,
  en,
  ja,
};

type I18nContextValue = {
  locale: Locale;
  messages: Messages;
  setLocale: (locale: Locale) => void;
  t: (path: string) => string;
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

const STORAGE_KEY = 'rana-fanpage-locale';

const resolvePath = (messages: Messages, path: string): string => {
  const segments = path.split('.');
  let current: any = messages;

  for (const segment of segments) {
    if (current && typeof current === 'object' && segment in current) {
      current = current[segment];
    } else {
      return path;
    }
  }

  return typeof current === 'string' ? current : path;
};

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window === 'undefined') return 'ko';
    const saved = window.localStorage.getItem(STORAGE_KEY) as Locale | null;
    if (saved && ['ko', 'ja', 'en'].includes(saved)) {
      return saved;
    }
    const navLang = window.navigator.language.slice(0, 2);
    if (navLang === 'ja') return 'ja';
    if (navLang === 'en') return 'en';
    return 'ko';
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(STORAGE_KEY, locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => {
    const messages = resources[locale];
    return {
      locale,
      messages,
      setLocale: setLocaleState,
      t: (path: string) => resolvePath(messages, path),
    };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = (): I18nContextValue => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

export const useTranslate = () => useI18n().t;
